import { ReactNode, useEffect, useMemo } from "react";
import {
  BankOutlined,
  EnvironmentOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { Button, Input, message, Modal, Radio, Select, Spin } from "antd";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useUpdateOrganization } from "../../api/organization";
import { useGetBanks } from "../../api/banks";
import {
  organizationEditSchema,
  OrganizationEditForm,
} from "../../schemas/formSchema";
import {
  OrganizationDetailPayload,
  OrganizationUpdatePayload,
  organizationBankIdsFromAccounts,
  parseOrganizationAddress,
  resolveOrganizationContactPerson,
} from "../../types/organization";

const REGIONS = [
  "Addis Ababa",
  "Amhara",
  "Benishangul",
  "Gambela",
  "Oromia",
  "Tigray",
  "SNNPE",
] as const;

const blankForm: OrganizationEditForm = {
  name: "",
  shortName: "",
  type: "",
  email: "",
  phoneNumber: "",
  address: {
    region: "",
    city: "",
    kifleKetema: "",
    woreda: "",
    kebele: "",
    houseNumber: "",
  },
  contactPerson: {
    fullName: "",
    email: "",
    phoneNumber: "",
    username: "",
  },
  bankIds: [],
  primaryBankId: undefined,
  bankAccountDetails: {},
};

function buildBankAccountDetails(
  organization?: OrganizationDetailPayload,
): OrganizationEditForm["bankAccountDetails"] {
  const details: OrganizationEditForm["bankAccountDetails"] = {};
  organization?.bankAccounts?.forEach((account) => {
    if (account.bankId == null) return;
    details[String(account.bankId)] = {
      accountName: account.accountName ?? "",
      accountNumber: account.accountNumber ?? "",
      accountBranch: account.accountBranch ?? "",
    };
  });
  return details;
}

function organizationToFormValues(
  organization?: OrganizationDetailPayload,
): OrganizationEditForm {
  if (!organization) return blankForm;

  const bankIds = organizationBankIdsFromAccounts(organization.bankAccounts);
  const primary =
    organization.bankAccounts?.find((a) => a.isPrimary)?.bankId ??
    bankIds[0];

  return {
    name: organization.name ?? "",
    shortName: organization.shortName ?? "",
    type: organization.type ?? "",
    email: organization.email ?? "",
    phoneNumber: organization.phoneNumber ?? organization.phoneNo ?? "",
    address: parseOrganizationAddress(organization.address),
    contactPerson: resolveOrganizationContactPerson(organization),
    bankIds,
    primaryBankId: primary,
    bankAccountDetails: buildBankAccountDetails(organization),
  };
}

function buildUpdatePayload(
  form: OrganizationEditForm,
  organization?: OrganizationDetailPayload,
): OrganizationUpdatePayload {
  const bankAccounts = form.bankIds.map((bankId: number) => {
    const existing = organization?.bankAccounts?.find(
      (a) => a.bankId === bankId,
    );
    const details = form.bankAccountDetails[String(bankId)] ?? {};

    return {
      ...(existing?.id != null ? { id: existing.id } : {}),
      bankId,
      accountName: details.accountName?.trim() || existing?.accountName,
      accountNumber:
        details.accountNumber?.trim() || existing?.accountNumber,
      accountBranch: details.accountBranch?.trim() || existing?.accountBranch,
      isPrimary: bankId === form.primaryBankId,
      isActive: existing?.isActive ?? true,
    };
  });

  return {
    name: (organization?.name ?? form.name).trim(),
    shortName: form.shortName?.trim() || undefined,
    type: (organization?.type ?? form.type?.trim()) || undefined,
    email: form.email?.trim() || undefined,
    phoneNumber: form.phoneNumber?.trim() || undefined,
    address: JSON.stringify(form.address),
    contactPerson: {
      fullName: form.contactPerson.fullName?.trim() || undefined,
      email: form.contactPerson.email?.trim() || undefined,
      phoneNumber: form.contactPerson.phoneNumber?.trim() || undefined,
      username: form.contactPerson.username?.trim() || undefined,
    },
    bankAccounts,
  };
}

function FormSection({
  title,
  icon,
  children,
  className = "",
}: {
  title: string;
  icon: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section
      className={`rounded-xl border border-gray-100 bg-gradient-to-b from-white to-gray-50/80 p-3 shadow-sm ${className}`}
    >
      <div className="flex items-center gap-2 mb-2 pb-2 border-b border-gray-100">
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-[#08453E]/10 text-[#08453E] text-sm">
          {icon}
        </span>
        <h4 className="text-sm font-semibold text-gray-900 m-0">{title}</h4>
      </div>
      <div className="space-y-2">{children}</div>
    </section>
  );
}

function Field({
  label,
  required,
  error,
  children,
}: {
  label: string;
  required?: boolean;
  error?: string;
  children: ReactNode;
}) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-700 mb-1">
        {label}
        {required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      {children}
      {error && <p className="text-xs text-red-600 mt-1 mb-0">{error}</p>}
    </div>
  );
}

type OrganizationEditModalProps = {
  open: boolean;
  hideModal: () => void;
  orgId?: number | string;
  organization?: OrganizationDetailPayload;
};

export function OrganizationEditModal({
  open,
  hideModal,
  orgId,
  organization,
}: OrganizationEditModalProps) {
  const { mutateAsync: updateOrganization, isPending } = useUpdateOrganization();
  const { data: banks = [], isLoading: loadingBanks } = useGetBanks({
    activeOnly: false,
  });

  const {
    handleSubmit,
    control,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<OrganizationEditForm>({
    resolver: zodResolver(organizationEditSchema) as any,
    defaultValues: blankForm,
    mode: "onChange",
  });

  const bankIds: number[] = watch("bankIds") ?? [];
  const primaryBankId = watch("primaryBankId");

  useEffect(() => {
    if (!open) return;
    reset(organizationToFormValues(organization));
  }, [open, organization, reset]);

  const linkedBankIds = useMemo(
    () => new Set(organizationBankIdsFromAccounts(organization?.bankAccounts)),
    [organization],
  );

  const bankOptions = useMemo(
    () =>
      banks
        .filter((b) => b.isActive || linkedBankIds.has(b.id))
        .map((b) => ({
          value: b.id,
          label: `${b.bankName}${b.code ? ` (${b.code})` : ""}${
            b.isActive ? "" : " — inactive"
          }`,
          disabled: !b.isActive,
        })),
    [banks, linkedBankIds],
  );

  const banksById = useMemo(
    () => new Map(banks.map((b) => [b.id, b])),
    [banks],
  );

  const onSubmit: SubmitHandler<OrganizationEditForm> = async (form) => {
    if (!orgId) {
      message.error("No organization id");
      return;
    }

    const payload = buildUpdatePayload(form, organization);

    try {
      const result = await updateOrganization({ id: orgId, payload });
      if (result.data?.status === "success" || result.status === 200) {
        message.success("Organization updated successfully");
        hideModal();
      } else {
        message.error("Update failed");
      }
    } catch (err: any) {
      const apiMessage =
        err?.response?.data?.message ||
        err?.response?.data ||
        err?.message ||
        "Update failed";
      message.error(
        typeof apiMessage === "string" ? apiMessage : "Update failed",
      );
    }
  };

  const inputClass = "rounded-lg";

  return (
    <Modal
      destroyOnClose
      open={open}
      centered
      width="min(960px, 96vw)"
      onCancel={hideModal}
      className="organization-edit-modal"
      styles={{
        header: { padding: "12px 20px 8px", borderBottom: "1px solid #f0f0f0" },
        body: {
          maxHeight: "min(58vh, 520px)",
          overflowY: "auto",
          padding: "10px 20px 4px",
        },
        footer: {
          padding: "8px 20px 12px",
          borderTop: "1px solid #f0f0f0",
        },
      }}
      title={
        <div className="pr-8">
          <p className="text-lg font-semibold text-gray-900 m-0">
            Edit organization
          </p>
          <p className="text-xs text-gray-500 mt-0.5 mb-0 font-normal">
            Update profile, linked banks, and contact details
          </p>
        </div>
      }
      footer={
        <div className="flex flex-col-reverse sm:flex-row justify-end gap-2">
          <Button
            onClick={hideModal}
            className="!h-9 !rounded-full !px-5 !border-gray-300 !text-gray-700 font-medium"
          >
            Cancel
          </Button>
          <Button
            type="primary"
            onClick={handleSubmit(onSubmit)}
            className="!h-9 !rounded-full !px-6 !bg-[#08453E] hover:!bg-[#0a554c] !border-none font-medium"
            loading={isPending}
            disabled={isPending}
          >
            Save changes
          </Button>
        </div>
      }
    >
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="grid grid-cols-1 lg:grid-cols-2 gap-3 text-gray-900"
      >
        <FormSection
          title="General"
          icon={<UserOutlined />}
          className="lg:col-span-1"
        >
          <Field label="Name" required error={errors.name?.message}>
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  disabled
                  className={inputClass}
                  placeholder="Organization name"
                />
              )}
            />
          </Field>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <Field label="Short name">
              <Controller
                name="shortName"
                control={control}
                render={({ field }) => (
                  <Input {...field} className={inputClass} />
                )}
              />
            </Field>
            <Field label="Organization type">
              <Controller
                name="type"
                control={control}
                render={({ field }) => (
                  <Input {...field} disabled className={inputClass} />
                )}
              />
            </Field>
          </div>
          <Field label="Email" error={errors.email?.message}>
            <Controller
              name="email"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  className={inputClass}
                  type="email"
                />
              )}
            />
          </Field>
          <Field label="Phone" error={errors.phoneNumber?.message}>
            <Controller
              name="phoneNumber"
              control={control}
              render={({ field }) => (
                <Input {...field} className={inputClass} placeholder="0912345678" />
              )}
            />
          </Field>
        </FormSection>

        <FormSection title="Address" icon={<EnvironmentOutlined />}>
          <Field label="Region">
            <Controller
              name="address.region"
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  value={field.value || undefined}
                  placeholder="Select region"
                  className="w-full"
                  options={REGIONS.map((region) => ({
                    label: region,
                    value: region,
                  }))}
                />
              )}
            />
          </Field>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <Field label="City">
              <Controller
                name="address.city"
                control={control}
                render={({ field }) => (
                  <Input {...field} className={inputClass} />
                )}
              />
            </Field>
            <Field label="Kifle ketema">
              <Controller
                name="address.kifleKetema"
                control={control}
                render={({ field }) => (
                  <Input {...field} className={inputClass} />
                )}
              />
            </Field>
            <Field label="Woreda">
              <Controller
                name="address.woreda"
                control={control}
                render={({ field }) => (
                  <Input {...field} className={inputClass} />
                )}
              />
            </Field>
            <Field label="Kebele">
              <Controller
                name="address.kebele"
                control={control}
                render={({ field }) => (
                  <Input {...field} className={inputClass} />
                )}
              />
            </Field>
          </div>
          <Field label="House number">
            <Controller
              name="address.houseNumber"
              control={control}
              render={({ field }) => (
                <Input {...field} className={inputClass} />
              )}
            />
          </Field>
        </FormSection>

        <FormSection title="Contact person" icon={<UserOutlined />}>
          <Field label="Full name">
            <Controller
              name="contactPerson.fullName"
              control={control}
              render={({ field }) => (
                <Input {...field} className={inputClass} />
              )}
            />
          </Field>
          <Field
            label="Email"
            error={errors.contactPerson?.email?.message}
          >
            <Controller
              name="contactPerson.email"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  className={inputClass}
                  type="email"
                />
              )}
            />
          </Field>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <Field
              label="Phone"
              error={errors.contactPerson?.phoneNumber?.message}
            >
              <Controller
                name="contactPerson.phoneNumber"
                control={control}
                render={({ field }) => (
                  <Input {...field} className={inputClass} placeholder="0912345678" />
                )}
              />
            </Field>
            <Field label="Username">
              <Controller
                name="contactPerson.username"
                control={control}
                render={({ field }) => (
                  <Input {...field} className={inputClass} />
                )}
              />
            </Field>
          </div>
        </FormSection>

        <FormSection
          title="Linked banks"
          icon={<BankOutlined />}
          className="lg:col-span-2"
        >
          <Field
            label="Banks"
            required
            error={errors.bankIds?.message as string | undefined}
          >
            {loadingBanks ? (
              <div className="flex items-center gap-2 py-2 text-gray-500">
                <Spin size="small" />
                <span className="text-sm">Loading banks…</span>
              </div>
            ) : (
              <Controller
                name="bankIds"
                control={control}
                render={({ field }) => (
                  <Select
                    mode="multiple"
                    className="w-full"
                    placeholder="Select one or more banks"
                    value={field.value}
                    onChange={(ids) => {
                      field.onChange(ids);
                      if (
                        ids.length > 0 &&
                        (!primaryBankId || !ids.includes(primaryBankId))
                      ) {
                        setValue("primaryBankId", ids[0], {
                          shouldValidate: true,
                        });
                      }
                      if (ids.length === 0) {
                        setValue("primaryBankId", undefined, {
                          shouldValidate: true,
                        });
                      }
                    }}
                    options={bankOptions}
                    optionFilterProp="label"
                    maxTagCount="responsive"
                    showSearch
                    allowClear
                  />
                )}
              />
            )}
            <p className="text-xs text-gray-500 mt-1 mb-0">
              Choose all financing banks this organization works with.
            </p>
          </Field>

          {bankIds.length > 0 && (
            <div className="mt-1 space-y-2">
              <Field
                label="Primary bank"
                error={errors.primaryBankId?.message}
              >
                <Controller
                  name="primaryBankId"
                  control={control}
                  render={({ field }) => (
                    <Radio.Group
                      {...field}
                      className="flex flex-wrap gap-x-4 gap-y-1"
                      value={field.value}
                      onChange={(e) => field.onChange(e.target.value)}
                    >
                      {bankIds.map((id: number) => {
                        const bank = banksById.get(id);
                        return (
                          <Radio key={id} value={id} className="!m-0">
                            <span className="text-sm text-gray-800">
                              {bank?.bankName ?? `Bank #${id}`}
                            </span>
                          </Radio>
                        );
                      })}
                    </Radio.Group>
                  )}
                />
              </Field>

              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2">
                {bankIds.map((id: number) => {
                  const bank = banksById.get(id);
                  const bankKey = String(id);
                  const bankErrors = errors.bankAccountDetails?.[bankKey];
                  return (
                    <div
                      key={id}
                      className="rounded-lg border border-gray-200 bg-white p-2.5 shadow-sm"
                    >
                      <p className="text-xs font-semibold text-[#08453E] mb-2">
                        {bank?.bankName ?? `Bank #${id}`}
                        {bank?.code ? (
                          <span className="text-gray-400 font-normal ml-1">
                            ({bank.code})
                          </span>
                        ) : null}
                      </p>
                      <div className="space-y-1.5">
                        <Field
                          label="Account name"
                          error={bankErrors?.accountName?.message}
                        >
                          <Controller
                            name={`bankAccountDetails.${id}.accountName`}
                            control={control}
                            render={({ field }) => (
                              <Input
                                {...field}
                                className={inputClass}
                                placeholder="Account holder name"
                                onChange={(e) =>
                                  field.onChange(
                                    e.target.value.replace(/[^A-Za-z\s]/g, ""),
                                  )
                                }
                              />
                            )}
                          />
                        </Field>
                        <Field
                          label="Account number"
                          error={bankErrors?.accountNumber?.message}
                        >
                          <Controller
                            name={`bankAccountDetails.${id}.accountNumber`}
                            control={control}
                            render={({ field }) => (
                              <Input
                                {...field}
                                className={inputClass}
                                placeholder="Account number"
                                onChange={(e) =>
                                  field.onChange(
                                    e.target.value.replace(/\D/g, ""),
                                  )
                                }
                              />
                            )}
                          />
                        </Field>
                        <Field label="Branch">
                          <Controller
                            name={`bankAccountDetails.${id}.accountBranch`}
                            control={control}
                            render={({ field }) => (
                              <Input
                                {...field}
                                className={inputClass}
                                placeholder="Branch name"
                              />
                            )}
                          />
                        </Field>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </FormSection>
      </form>
    </Modal>
  );
}
